import React, { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import AICoachChat from "../../components/AICoach/AICoachChat";
import { DigipingMark } from "../../digiping";
import "../../components/AICoach/DigipingChat.css";
import "./LiveTrainingFeedbackSheet.css";

export function LiveTrainingFeedbackSheet({
  open,
  onClose,
  teId,
  tsId,
  userId: _userId,
  peerId,
  isCoachView,
  presentPlayers = [],
  isLandscape = false,
  brandPrimary = "#0877BC",
  initialTab = "session",
  grId = null,
  sessionCoachUserId = null,
  /** When true, Digiping designs the live session plan + can mark presence. */
  sessionPlanAssist = false,
  bootstrapSeed = null,
  stationName = null,
  exerciseName = null,
  tsgId = null,
  presentPlayerNames = null,
  groupRosterNotPresentNames = null,
  similarLastSession = null,
  /** Current live session plan blocks (keeps Digiping aware of existing exercises). */
  currentSessionPlan = null,
  clubId = null,
  clubName = null,
  groupName = null,
  liveTraining = false,
  sessionDate = null,
  sessionDuration = null,
}) {
  const { t } = useTranslation();
  const sheetRef = useRef(null);

  useEffect(() => {
    if (!open || typeof window === "undefined" || !window.visualViewport) return undefined;
    const vv = window.visualViewport;
    const syncKeyboardOffset = () => {
      const sheet = sheetRef.current;
      if (!sheet) return;
      const keyboardOffset = Math.max(
        0,
        window.innerHeight - vv.height - vv.offsetTop
      );
      sheet.style.setProperty("--lt-feedback-kb-offset", `${keyboardOffset}px`);
    };
    vv.addEventListener("resize", syncKeyboardOffset);
    vv.addEventListener("scroll", syncKeyboardOffset);
    syncKeyboardOffset();
    return () => {
      vv.removeEventListener("resize", syncKeyboardOffset);
      vv.removeEventListener("scroll", syncKeyboardOffset);
      sheetRef.current?.style.removeProperty("--lt-feedback-kb-offset");
    };
  }, [open]);

  const sessionContext = useMemo(() => {
    const ctx = {
      te_id: teId || null,
      ts_id: tsId || null,
      gr_id: grId || null,
      live_training: Boolean(liveTraining || sessionPlanAssist),
    };
    if (clubId) ctx.club_id = clubId;
    if (clubName) ctx.club_name = clubName;
    if (groupName) ctx.group_name = groupName;
    if (sessionDate) ctx.session_date = sessionDate;
    if (sessionDuration != null) ctx.session_duration = sessionDuration;
    if (sessionPlanAssist || liveTraining) {
      ctx.session_plan = true;
      ctx.page = "live_training";
      ctx.station_name = stationName || null;
      ctx.exercise_name = exerciseName || null;
      ctx.tsg_id = tsgId || null;
      ctx.player_search_scope = "club";
      ctx.restrict_players_to_club = true;
      if (grId) ctx.restrict_players_to_group = true;
      if (Array.isArray(presentPlayerNames)) {
        ctx.present_player_names = presentPlayerNames;
      }
      if (Array.isArray(groupRosterNotPresentNames)) {
        ctx.group_roster_not_present_names = groupRosterNotPresentNames;
      }
      if (similarLastSession) {
        ctx.similar_last_session = similarLastSession;
      }
      const names = currentSessionPlan?.planning_exercise_names;
      const blocks = currentSessionPlan?.current_session_blocks;
      if (Array.isArray(names) && names.length > 0) {
        ctx.planning_exercise_names = names;
      }
      if (Array.isArray(blocks) && blocks.length > 0) {
        ctx.current_session_blocks = blocks;
        ctx.session_plan_block_count =
          currentSessionPlan?.block_count ?? blocks.length;
      }
    }
    return ctx;
  }, [
    teId,
    tsId,
    grId,
    sessionPlanAssist,
    liveTraining,
    clubId,
    clubName,
    groupName,
    sessionDate,
    sessionDuration,
    stationName,
    exerciseName,
    tsgId,
    presentPlayerNames,
    groupRosterNotPresentNames,
    similarLastSession,
    currentSessionPlan,
  ]);

  if (!open || typeof document === "undefined") return null;

  const headerSub = sessionPlanAssist
    ? t(
        "liveTrainingPlanPingfloSub",
        "Design the session · create blocks · mark who is present"
      )
    : isCoachView
      ? t("liveTrainingFeedbackSubCoachShort") ||
        t("digipingChatPresenceOrder") ||
        "Pingflo · Players · Teams"
      : t("liveTrainingFeedbackSub") || "Coach & Pingflo";

  const portalStyle = {
    "--brand-primary": brandPrimary,
    "--lt-brand": brandPrimary,
    "--dp-primary": brandPrimary,
  };

  const defaultTab =
    initialTab === "digiping" || sessionPlanAssist
      ? "digiping"
      : isCoachView
        ? "people"
        : "digiping";

  const sheet = (
    <div
      className={`lt-fb-sheet-portal digiping-chat${
        isLandscape ? " lt-fb-sheet-portal--landscape" : ""
      }`}
      style={portalStyle}
      role="presentation"
      onClick={onClose}
    >
      <div
        ref={sheetRef}
        className="lt-fb-sheet lt-fb-sheet--digiping digiping-chat__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Digiping"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="digiping-chat__header">
          <div className="digiping-chat__brand">
            <div className="digiping-chat__avatar digiping-chat__avatar--sm" title="Digiping">
              <DigipingMark size={26} />
            </div>
            <div className="digiping-chat__brand-text">
              <span className="digiping-chat__label">
                {sessionPlanAssist ? "Pingflo" : "Digiping"}
              </span>
              <span className="digiping-chat__tag">{headerSub}</span>
            </div>
          </div>
          <div className="digiping-chat__actions">
            <button
              type="button"
              className="digiping-chat__icon-btn"
              onClick={onClose}
              aria-label={t("close")}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </header>

        <div className="dp-session-pane dp-session-pane--digiping">
          <AICoachChat
            mode="embedded"
            open={open}
            onRequestClose={onClose}
            sessionContext={sessionContext}
            hideHeader
            presentPlayers={presentPlayers}
            sessionCoachUserId={sessionCoachUserId || peerId}
            initialPeoplePeerId={!isCoachView ? peerId || sessionCoachUserId : null}
            defaultTab={defaultTab}
            bootstrapSeed={sessionPlanAssist ? bootstrapSeed : null}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}

export default LiveTrainingFeedbackSheet;
